import {
  Button,
  Card,
  CardActions,
  CardContent,
  Grid,
  IconButton,
  Menu,
  MenuItem,
  Typography,
} from '@mui/material';
import { FC, Fragment } from 'react';
import { AudienceSet, AudienceSetTargetsData } from '../../../../../types';
import { numberShortener } from '../../../../../utils/numberFormatter';
import { PrimaryButton } from '../../../../Buttons';
import { constantStringToHumanized } from '../../../../../utils/stringModifier';
import PopupState, { bindMenu, bindTrigger } from 'material-ui-popup-state';
import { DEFAULT_COLOR_THEME } from '../../../../../utils';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';

interface AudienceListProps {
  target: AudienceSetTargetsData;
  withAction?: boolean;
  onSelectTarget?: (
    flexibleIndex: number,
    target: AudienceSetTargetsData,
  ) => void;
  formValues?: AudienceSet;
  search?: boolean;
  onRemoveAudience?: (args: {
    target: AudienceSetTargetsData;
    targetType: string;
    flexibleIndex?: number;
  }) => void;
  flexibleIndex?: number;
  targetType?:
    | 'flexible_spec'
    | 'exclusions'
    | 'custom_audiences'
    | 'excluded_custom_audiences'
    | null;
}

const AudienceList: FC<AudienceListProps> = ({
  target,
  withAction = false,
  onSelectTarget,
  formValues,
  search = false,
  onRemoveAudience,
  flexibleIndex,
  targetType = null,
}) => {
  const getType = () => {
    if (target?.path?.includes('Interests')) return 'Interests';

    return constantStringToHumanized(target?.type || target?.subtype || '');
  };

  const includedTargetIds = formValues?.targets?.flexible_spec?.[0]?.[
    target.type
  ]?.map((target: AudienceSetTargetsData) => target.id);

  const included = includedTargetIds?.includes(target.id);

  const hideInclude = Object?.keys(formValues?.targets?.exclusions || {})
    ?.map((key: string) => {
      if (formValues?.targets?.exclusions[key]?.length === 0) return '';

      return key;
    })
    ?.includes(target.type);

  return (
    <Card>
      <CardContent sx={{ py: '5px !important' }}>
        <Grid
          container
          spacing={2}
          sx={{ alignItems: 'center', display: 'flex' }}
        >
          <Grid item xs={12} sm={8} sx={{ flexDirection: 'column' }}>
            <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
              {target.name}
            </Typography>

            <Typography variant="body2">{target.id}</Typography>

            {target.description ? (
              <Typography variant="body2" component="div">
                {target.description || ''}
              </Typography>
            ) : null}

            {target.audience_size_upper_bound ||
            target.approximate_count_lower_bound ||
            target.coverage_lower_bound ? (
              <Typography variant="body2" component="div">
                {`Est. Audience Size: ${numberShortener(
                  target.audience_size_lower_bound ||
                    target.approximate_count_lower_bound ||
                    target.coverage_lower_bound ||
                    0,
                )} - ${numberShortener(
                  target.audience_size_upper_bound ||
                    target.approximate_count_upper_bound ||
                    target.coverage_upper_bound ||
                    0,
                )}`}
              </Typography>
            ) : null}
          </Grid>

          <Grid
            item
            xs={12}
            sm={search ? 4 : 2}
            sx={{ flexDirection: 'column' }}
          >
            <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
              Type
            </Typography>

            <Typography variant="body2">{getType()}</Typography>
          </Grid>

          {!search && withAction ? (
            <Grid item xs={12} sm={2}>
              <IconButton
                onClick={() =>
                  onRemoveAudience({
                    target,
                    targetType,
                    flexibleIndex,
                  })
                }
              >
                <DeleteOutlineOutlinedIcon sx={{ color: 'red' }} />
              </IconButton>
            </Grid>
          ) : null}
        </Grid>
      </CardContent>

      {withAction ? (
        <CardActions sx={{ justifyContent: 'flex-end' }}>
          {search ? (
            !hideInclude ? (
              <>
                <PrimaryButton
                  title={included ? 'Included' : 'Include'}
                  type="button"
                  handleOnClick={() => onSelectTarget(0, target)}
                  disabled={included}
                  variant="text"
                />

                {formValues?.targets?.flexible_spec?.length > 0 ? (
                  <>
                    <PopupState variant="popover" popupId="demo-popup-menu">
                      {(popupState) => (
                        <Fragment>
                          <Button
                            variant="text"
                            {...bindTrigger(popupState)}
                            sx={{
                              '&:hover': {
                                backgroundColor: 'transparent',
                                color: DEFAULT_COLOR_THEME,
                                boxShadow: '0',
                              },
                              boxShadow: '0',
                              backgroundColor: 'transparent',
                              color: DEFAULT_COLOR_THEME,
                            }}
                            //startIcon={<AddPhotoAlternateOutlinedIcon />}
                            //disabled={!format}
                          >
                            Narrow Audience
                          </Button>

                          <Menu {...bindMenu(popupState)}>
                            {formValues?.targets?.flexible_spec?.map(
                              (_, index: number) => {
                                const selected =
                                  formValues?.targets?.flexible_spec?.[
                                    index + 1
                                  ]?.[target.type]
                                    ?.map(
                                      (target: AudienceSetTargetsData) =>
                                        target.id,
                                    )
                                    ?.includes(target.id);

                                const isLastIndex =
                                  formValues?.targets?.flexible_spec?.length ===
                                  index + 1;

                                return (
                                  <MenuItem
                                    key={index + 1}
                                    onClickCapture={popupState.close}
                                    onClick={() =>
                                      onSelectTarget(index + 1, target)
                                    }
                                    disabled={selected}
                                  >
                                    {isLastIndex
                                      ? 'Narrow Further'
                                      : `Add to Segment ${index + 2}`}
                                  </MenuItem>
                                );
                              },
                            )}
                          </Menu>
                        </Fragment>
                      )}
                    </PopupState>
                  </>
                ) : null}
              </>
            ) : null
          ) : null}
        </CardActions>
      ) : null}
    </Card>
  );
};

export default AudienceList;
